import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, TextField, TextArea, SelectField, Section, EmptyState } from "@/components/ui-kit";
import { Mail, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const canEdit = ["admin", "dirigeant"].includes(user?.role);

  const load = () => {
    api.get("/templates").then((r) => {
      setTemplates(r.data);
      if (!selected && r.data[0]) setSelected(r.data[0]);
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/templates/${selected.id}`, { nom: selected.nom, sujet: selected.sujet, corps: selected.corps, destinataire_type: selected.destinataire_type });
      toast.success("Modèle enregistré");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8" data-testid="templates-page">
      <Toaster position="top-right" richColors />
      <PageHeader title="Modèles d'emails" subtitle="Configuration" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Section title={`Modèles (${templates.length})`}>
            <div className="max-h-[600px] overflow-y-auto">
              {templates.length === 0 ? <EmptyState icon={Mail} title="Aucun modèle" /> :
                templates.map((t) => (
                  <button key={t.id} onClick={() => setSelected(t)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${selected?.id === t.id ? "bg-slate-50 border-l-2 border-l-[#B8873A]" : ""}`}
                    data-testid={`template-${t.slug}`}>
                    <div className="text-sm font-semibold text-[#111111]">{t.nom}</div>
                    <div className="text-xs text-slate-500 mt-1">Destinataire : {t.destinataire_type}</div>
                  </button>
                ))}
            </div>
          </Section>
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-sm border border-slate-200 p-6">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Modèle</div>
              <h2 className="text-xl font-bold text-[#111111] mb-4" style={{fontFamily:"Cabinet Grotesk"}}>{selected.nom}</h2>
              <div className="space-y-4">
                <TextField label="Nom" value={selected.nom} onChange={(e) => setSelected({ ...selected, nom: e.target.value })} disabled={!canEdit} testid="template-nom" />
                <SelectField label="Destinataire" value={selected.destinataire_type} onChange={(e) => setSelected({ ...selected, destinataire_type: e.target.value })} disabled={!canEdit}
                  options={[{ value: "client", label: "Client" }, { value: "promoteur", label: "Promoteur" }]} testid="template-destinataire" />
                <TextField label="Sujet" value={selected.sujet} onChange={(e) => setSelected({ ...selected, sujet: e.target.value })} disabled={!canEdit} testid="template-sujet" />
                <TextArea label="Corps du message" rows={14} value={selected.corps} onChange={(e) => setSelected({ ...selected, corps: e.target.value })} disabled={!canEdit} testid="template-corps" />
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-sm text-xs text-slate-600">
                  <div className="font-semibold mb-1">Variables disponibles :</div>
                  <div className="font-mono text-[11px]">
                    {"{{client_prenom}} {{client_nom}} {{promoteur}} {{programme}} {{lot}} {{prix}} {{date_signature}} {{numero_facture}} {{commission}} {{notaire}} {{date_notaire}} {{banque}}"}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex justify-end">
                    <PrimaryButton onClick={save} disabled={saving} data-testid="save-template-button"><span className="flex items-center gap-2"><Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}</span></PrimaryButton>
                  </div>
                )}
              </div>
            </div>
          ) : <EmptyState icon={Mail} title="Sélectionnez un modèle" />}
        </div>
      </div>
    </div>
  );
}
